import { useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import { useTable, useFilters, useSortBy } from "react-table";

import Card from "@mui/material/Card";
import CircularProgress from "@mui/material/CircularProgress";
import FormControlLabel from "@mui/material/FormControlLabel";
import IconButton from "@mui/material/IconButton";
import Switch from "@mui/material/Switch";
import Tooltip from "@mui/material/Tooltip";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import RestartAltIcon from "@mui/icons-material/RestartAlt";

import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";

import "./ProjectTable.css";

function filtroTextoAnd(rows, ids, filterValue) {
  const termos = String(filterValue || "")
    .toLowerCase()
    .trim()
    .split(/\s+/)
    .filter(Boolean);

  if (termos.length === 0) return rows;

  return rows.filter((row) => {
    const texto = String(row.values[ids[0]] || "").toLowerCase();
    return termos.every((termo) => texto.includes(termo));
  });
}

filtroTextoAnd.autoRemove = (val) => !val;

function DefaultColumnFilter({ column }) {
  const { filterValue, setFilter } = column;

  return (
    <input
      className="project-table-filter"
      value={filterValue || ""}
      onChange={(e) => setFilter(e.target.value || undefined)}
      placeholder="Filtrar..."
    />
  );
}

DefaultColumnFilter.propTypes = {
  column: PropTypes.object.isRequired,
};

function safeReadState(storageKey) {
  try {
    const raw = localStorage.getItem(storageKey);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

function safeWriteState(storageKey, value) {
  try {
    localStorage.setItem(storageKey, JSON.stringify(value));
  } catch {
    // ignore
  }
}

function safeRemoveState(storageKey) {
  try {
    localStorage.removeItem(storageKey);
  } catch {
    // ignore
  }
}

export default function ProjectTable({
  tableKey,
  version,
  title,
  subtitle,
  columns,
  data,
  loading,
  emptyMessage,
  minWidth,
  maxHeight,
  rowActions,
  toolbarActions,
  defaultSortBy,
  showKeepConfig,
}) {
  const storageKey = `projectTable:${tableKey}:${version}`;
  const saved = useMemo(() => safeReadState(storageKey) || {}, [storageKey]);

  const [keepConfig, setKeepConfig] = useState(saved.keep !== false);
  const [widths, setWidths] = useState(saved.widths || {});
  const resizingRef = useRef(null);

  const columnsFinal = useMemo(() => {
    const base = Array.isArray(columns) ? columns : [];

    const cols = base.map((col) => {
      const id = col.id || col.accessor;
      const savedWidth = id ? widths[id] : null;
      const width = savedWidth || col.width || col.defaultWidth;

      return {
        ...col,
        width,
        minWidth: col.minWidth || 60,
        maxWidth: col.maxWidth || 900,
        Filter: col.Filter || DefaultColumnFilter,
        filter: col.filter || "textoAnd",
        disableFilters: col.filterable === false || col.disableFilters,
        disableSortBy: col.sortable === false || col.disableSortBy,
      };
    });

    if (Array.isArray(rowActions) && rowActions.length > 0) {
      cols.unshift({
        Header: "",
        id: "__actions",
        width: 42 + rowActions.length * 28,
        minWidth: 42,
        maxWidth: 220,
        disableFilters: true,
        disableSortBy: true,
        resizable: false,
        Cell: ({ row }) => (
          <MDBox display="flex" gap={0.5} alignItems="center">
            {rowActions
              .filter((a) => (typeof a.visible === "function" ? a.visible(row.original) : true))
              .map((a) => (
                <Tooltip title={a.tooltip || ""} key={a.id}>
                  <IconButton
                    size="small"
                    color={a.color || "primary"}
                    onClick={() => a.onClick?.(row.original)}
                    sx={{
                      border: "1px solid",
                      borderColor: `${a.color || "primary"}.main`,
                      borderRadius: 1,
                    }}
                  >
                    {a.icon}
                  </IconButton>
                </Tooltip>
              ))}
          </MDBox>
        ),
      });
    }

    return cols;
  }, [columns, rowActions, widths]);

  const defaultColumn = useMemo(
    () => ({
      Filter: DefaultColumnFilter,
      filter: "textoAnd",
    }),
    []
  );

  const sortTypes = useMemo(
    () => ({
      numPt: (rowA, rowB, columnId) => {
        const parse = (v) => {
          if (v == null) return Number.NEGATIVE_INFINITY;
          if (typeof v === "number") return v;
          const s = String(v).trim().replace(/\s+/g, " ").split(" ")[0];
          const n = Number(s.replace(",", "."));
          return Number.isFinite(n) ? n : Number.NEGATIVE_INFINITY;
        };

        const a = parse(rowA.values[columnId]);
        const b = parse(rowB.values[columnId]);

        if (a === b) return 0;
        return a > b ? 1 : -1;
      },
      textPt: (rowA, rowB, columnId) => {
        const a = String(rowA.values[columnId] ?? "").toLowerCase();
        const b = String(rowB.values[columnId] ?? "").toLowerCase();
        return a.localeCompare(b, "pt-PT");
      },
    }),
    []
  );

  const savedFilters = Array.isArray(saved.filters) ? saved.filters : [];
  const savedSortBy = Array.isArray(saved.sortBy) ? saved.sortBy : defaultSortBy || [];

  const tableInstance = useTable(
    {
      columns: columnsFinal,
      data: Array.isArray(data) ? data : [],
      defaultColumn,
      sortTypes,
      filterTypes: {
        textoAnd: filtroTextoAnd,
      },
      initialState: {
        filters: keepConfig ? savedFilters : [],
        sortBy: keepConfig ? savedSortBy : defaultSortBy || [],
      },
      autoResetFilters: false,
      autoResetSortBy: false,
    },
    useFilters,
    useSortBy
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
    state,
    setAllFilters,
    setSortBy,
  } = tableInstance;

  const activeFiltersCount = state.filters?.length || 0;

  useEffect(() => {
    if (!keepConfig) return;

    safeWriteState(storageKey, {
      keep: true,
      filters: state.filters || [],
      sortBy: state.sortBy || [],
      widths,
    });
  }, [storageKey, keepConfig, state.filters, state.sortBy, widths]);

  const resetConfig = () => {
    safeRemoveState(storageKey);
    setAllFilters([]);
    setSortBy(defaultSortBy || []);
    setWidths({});
    setKeepConfig(true);
  };

  const startResize = (e, column) => {
    if (column.resizable === false) return;

    e.preventDefault();
    e.stopPropagation();

    resizingRef.current = {
      id: column.id,
      startX: e.clientX,
      startWidth: Number(column.width || 120),
      minWidth: Number(column.minWidth || 60),
      maxWidth: Number(column.maxWidth || 900),
    };
  };

  useEffect(() => {
    const onMove = (e) => {
      const r = resizingRef.current;
      if (!r) return;

      const delta = e.clientX - r.startX;
      const next = Math.max(r.minWidth, Math.min(r.maxWidth, r.startWidth + delta));

      setWidths((prev) => ({
        ...prev,
        [r.id]: next,
      }));
    };

    const onUp = () => {
      resizingRef.current = null;
    };

    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);

    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
  }, []);

  return (
    <Card>
      <MDBox p={2}>
        {(title || subtitle || showKeepConfig || toolbarActions?.length > 0) && (
          <MDBox
            mb={1.5}
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            gap={2}
            flexWrap="wrap"
          >
            <MDBox>
              {title && <MDTypography variant="h6">{title}</MDTypography>}
              {subtitle && (
                <MDTypography variant="caption" color="text">
                  {subtitle}
                </MDTypography>
              )}
            </MDBox>

            <MDBox display="flex" alignItems="center" gap={1} flexWrap="wrap">
              {showKeepConfig && (
                <FormControlLabel
                  control={
                    <Switch
                      size="small"
                      checked={keepConfig}
                      onChange={(e) => {
                        const checked = e.target.checked;
                        setKeepConfig(checked);

                        if (!checked) {
                          setAllFilters([]);
                          setSortBy(defaultSortBy || []);
                          safeWriteState(storageKey, { keep: false });
                        }
                      }}
                    />
                  }
                  label={
                    <span
                      style={{
                        fontSize: 12,
                        color: activeFiltersCount > 0 ? "#d97706" : undefined,
                        fontWeight: activeFiltersCount > 0 ? 600 : 400,
                      }}
                    >
                      Manter configuração
                      {activeFiltersCount > 0 ? ` (${activeFiltersCount} filtros)` : ""}
                    </span>
                  }
                />
              )}

              <Tooltip title="Repor configuração da tabela">
                <IconButton size="small" onClick={resetConfig}>
                  <RestartAltIcon fontSize="small" />
                </IconButton>
              </Tooltip>

              {(toolbarActions || []).map((a) => (
                <MDButton
                  key={a.id}
                  color={a.color || "info"}
                  variant={a.variant || "gradient"}
                  size="small"
                  disabled={!!a.disabled}
                  onClick={a.onClick}
                >
                  {a.label}
                </MDButton>
              ))}
            </MDBox>
          </MDBox>
        )}

        {loading && (
          <MDBox py={2} display="flex" justifyContent="center">
            <CircularProgress size={24} />
          </MDBox>
        )}

        <div
          className="project-table-wrap"
          style={{
            maxHeight: maxHeight || undefined,
          }}
        >
          <table
            {...getTableProps()}
            className="project-table"
            style={{
              minWidth: minWidth || 900,
            }}
          >
            <thead>
              {headerGroups.map((hg) => (
                <tr {...hg.getHeaderGroupProps()} key={hg.id}>
                  {hg.headers.map((col) => {
                    const filtered = (state.filters || []).some((f) => f.id === col.id);
                    const width = col.width || 120;

                    return (
                      <th
                        {...col.getHeaderProps(col.getSortByToggleProps())}
                        key={col.id}
                        className={filtered ? "filtered" : ""}
                        style={{
                          width,
                          minWidth: col.minWidth || width,
                          maxWidth: col.maxWidth || width,
                          position: "sticky",
                        }}
                      >
                        <MDBox display="flex" alignItems="center" gap={0.5}>
                          {col.render("Header")}
                          {filtered && <FilterAltIcon sx={{ fontSize: 14 }} />}
                          {col.isSorted ? (col.isSortedDesc ? " ↓" : " ↑") : ""}
                        </MDBox>

                        {!col.disableFilters && (
                          <div onClick={(e) => e.stopPropagation()} style={{ marginTop: 4 }}>
                            {col.canFilter ? col.render("Filter") : null}
                          </div>
                        )}

                        {col.resizable !== false && (
                          <span
                            className="project-table-resizer"
                            onMouseDown={(e) => startResize(e, col)}
                          />
                        )}
                      </th>
                    );
                  })}
                </tr>
              ))}
            </thead>

            <tbody {...getTableBodyProps()}>
              {rows.map((row) => {
                prepareRow(row);

                return (
                  <tr {...row.getRowProps()} key={row.id}>
                    {row.cells.map((cell) => (
                      <td
                        {...cell.getCellProps()}
                        key={cell.column.id}
                        style={{
                          textAlign: cell.column.align || "left",
                          width: cell.column.width,
                          minWidth: cell.column.minWidth || cell.column.width,
                          maxWidth: cell.column.maxWidth || cell.column.width,
                        }}
                      >
                        {cell.render("Cell")}
                      </td>
                    ))}
                  </tr>
                );
              })}

              {rows.length === 0 && !loading && (
                <tr>
                  <td colSpan={columnsFinal.length} className="project-table-empty">
                    {emptyMessage || "Sem dados para apresentar."}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </MDBox>
    </Card>
  );
}

ProjectTable.defaultProps = {
  version: "v1",
  title: "",
  subtitle: "",
  data: [],
  loading: false,
  emptyMessage: "Sem dados para apresentar.",
  minWidth: 900,
  maxHeight: null,
  rowActions: [],
  toolbarActions: [],
  defaultSortBy: [],
  showKeepConfig: true,
};

ProjectTable.propTypes = {
  tableKey: PropTypes.string.isRequired,
  version: PropTypes.string,
  title: PropTypes.string,
  subtitle: PropTypes.string,
  columns: PropTypes.array.isRequired,
  data: PropTypes.array,
  loading: PropTypes.bool,
  emptyMessage: PropTypes.string,
  minWidth: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  maxHeight: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  rowActions: PropTypes.array,
  toolbarActions: PropTypes.array,
  defaultSortBy: PropTypes.array,
  showKeepConfig: PropTypes.bool,
};
